import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds } from "../../helpers/karkhanaEnabled";
import { closeLoom, getRounds, listKarkhanaWorkTypes, previewLoomEntry, reopenLoom, saveLoomEntry } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { readRoster, refreshRoster } from "./punches/roster";
import { partName, shiftDay, todayIso, sortWorkTypes } from "./rates/rateModel";
import LoomSheet from "./rounds/LoomSheet";
import { entryBody, pasteColumn, SheetLine } from "./sheet/sheetModel";
import "./rounds/karkhana-rounds.scss";
import "./sheet/karkhana-sheet.scss";
import "./workers/karkhana-workers.scss";
import PageHeader from "./ui/PageHeader";
import { LangToggle, useKT } from "./i18n/kt";
import { buildDaily, changesPeople, keepConfirmed, countRows, dayLabel, DailyFilter, DailyRow, keepTyped, matchesFilter, savable, waitingForPart, waitingForPeople } from "./daily/dailyModel";
import DailyList from "./daily/DailyList";
import DailyTable from "./daily/DailyTable";
import RowActions from "./daily/RowActions";
import { useWidthTier } from "./daily/useWidthTier";
import "./daily/karkhana-daily.scss";
import { withoutRetired } from "./sarees/sareeEditModel";
import { kp } from "./karkhanaPaths";
import { useAddressOverlay, useAddressParam, withParams } from "./ui/addressState";
import { OutsideRoundNote, roundLoomIds, RoundScopeSwitch, useRoundScope } from "./rounds/myRound";
import { applyDraft, savedPartsByLoom, cleanOldDrafts, draftLines, registerDraftFlush, draftScope, otherDrafts, patchDraftLine, readDraft, writeDraft } from "./daily/dailyDraft";

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

type Calc = { status?: string; total?: string | number | null };

const FILTERS: DailyFilter[] = ["ALL", "LEFT", "DONE", "CLOSED"];


/**
 * Daily work: every loom in one list, the DAILY WORK tab of the old Google Sheet. Type inches, save;
 * a closed loom is one long-press, a loom with no saree has Start saree. A phone gets the list, a
 * tablet in landscape the list beside the loom's page, a desktop the full table.
 */
const KarkhanaDailyWorkPage = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const location = useLocation();
  const tier = useWidthTier();
  const auth = readAuth();
  const role = auth?.role;
  const orgId: string = auth?.organizationResponse?._id || "org";
  const [date, setDate] = useState(() => new URLSearchParams(location.search).get("date") || todayIso());
  const [data, setData] = useState<any>(null);
  const [rows, setRows] = useState<DailyRow[]>([]);
  const [lines, setLines] = useState<SheetLine[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [roster, setRoster] = useState(() => readRoster(orgId));
  // Which looms show (?filter=…) is part of the address, so Back and a reload keep it.
  const [filterParam, setFilter] = useAddressParam("filter", "ALL");
  const filter: DailyFilter = (FILTERS as string[]).includes(filterParam) ? (filterParam as DailyFilter) : "ALL";
  // A supervisor with a usual round sees their looms (My round) or every loom (All looms), remembered on this device.
  const [roundScope, setRoundScope] = useRoundScope();
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  // Tablet: the loom shown beside the list. Desktop: the loom open in the drawer.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Desktop: the loom open in the drawer is in the address (?entry=…): Back closes it, a reload reopens it.
  const entry = useAddressOverlay("entry");
  const drawerId = entry.value;
  const setDrawerId = (id: string | null) => (!id ? entry.close() : drawerId ? entry.switchTo(id) : entry.open(id));
  const [previews, setPreviews] = useState<Map<string, Calc>>(new Map());
  // People picked for today on this screen, including anyone just added (not in the roster yet).
  const [picked, setPicked] = useState<Map<string, { id: string; code: string; name: string }>>(new Map());
  // Looms whose weaver and helper were confirmed or changed on this screen today; nothing else saves.
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const confirmedRef = useRef<Set<string>>(confirmed);
  confirmedRef.current = confirmed;
  const rowsRef = useRef<DailyRow[]>([]);
  rowsRef.current = rows;
  const confirmLoom = (loomId: string) => setConfirmed((c) => new Set(c).add(loomId));
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const linesRef = useRef<SheetLine[]>([]);
  linesRef.current = lines;
  // The day the lines on screen belong to (the date can change before the new day has loaded).
  const linesDate = useRef<string | null>(null);
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  // "3 looms restored from this phone": the device copy put back when the day was opened.
  const [restoredNote, setRestoredNote] = useState<{ restored: number; restoredIds: string[]; alreadySaved: string[]; changedSaree: string[]; gone: number } | null>(null);
  // Other days with unsaved rows on this phone: "2 unsaved on 22 Oct".
  const [elsewhere, setElsewhere] = useState<{ date: string; n: number }[]>([]);
  const savingRef = useRef(false);
  const shownDate = useRef(date);
  shownDate.current = date;

  // Whose device copy this is (studio + user). Fixed for this screen: after a logout nothing more is written.
  const [scope] = useState(() => draftScope() || `${orgId}.me`);
  // Looms changed on this screen for the day on screen: only these are removed from the device copy
  // when they're saved or cleared; another tab's or the loom page's rows are left alone.
  const touched = useRef(new Set<string>());

  /** Writes the unsaved rows of the day on screen to this phone, merged by loom with what's stored. */
  const flushDraft = useCallback(() => {
    const day = linesDate.current;
    // Logged out (or someone else signed in) meanwhile: never write into the next person's phone.
    if (!day || draftScope() !== scope) return;
    const kept = draftLines(linesRef.current, confirmedRef.current);
    const typed = new Set(kept.map((l) => l.loomId));
    const drop = Array.from(touched.current).filter((id) => !typed.has(id));
    writeDraft(scope, day, kept, Array.from(pickedRef.current.values()), drop);
  }, [scope]);

  useEffect(() => {
    if (!canUseRounds(role)) history.replace("/dashboard");
  }, [role, history]);

  useEffect(() => {
    refreshRoster(orgId).then(setRoster).catch(() => undefined);
    listKarkhanaWorkTypes(0, 200)
      // Saree parts first in a natural order (S_1, S_2 … S_10), like the loom page.
      .then((res: any) => setTypes(sortWorkTypes((res?.data?.docs || []).filter((w: any) => w.active !== false))))
      .catch(() => setTypes([]));
  }, [orgId]);

  const defaultType = useMemo(() => types.find((w) => w.code === "S_1")?.id || types[0]?.id || "", [types]);

  const load = useCallback(
    async (keep = true) => {
      try {
        const res: any = await getRounds(date);
        // The date changed while this day was loading: its rows must not land on the other day.
        if (shownDate.current !== date) return;
        if (!res?.success) {
          notifications({ title: t("Error"), message: t(res?.message || "Could not load the looms"), type: "danger" });
          return;
        }
        const built = buildDaily(withoutRetired(res.data), defaultType, date);
        setData(withoutRetired(res.data));
        setRows(built.rows);
        if (keep && linesDate.current === date) {
          // Someone else saved or changed the people meanwhile: those looms ask again.
          setConfirmed((c) => keepConfirmed(c, rowsRef.current, built.rows));
          setLines(keepTyped(built.lines, linesRef.current));
        } else {
          // Opening a day: put back what this phone typed for it and didn't save.
          const draft = readDraft(scope, date);
          const back = applyDraft(built.lines, draft, savedPartsByLoom(res.data?.looms));
          // Rows the server already has (or whose saree changed) leave the device copy at the next write.
          touched.current = new Set(back.dropped);
          if (draft?.people?.length) {
            setPicked((m) => {
              const next = new Map(m);
              draft.people!.forEach((x) => !next.has(x.id) && next.set(x.id, x));
              return next;
            });
          }
          setConfirmed(back.confirmed);
          setLines(back.lines);
          setRestoredNote(draft && (back.restored || back.alreadySaved.length || back.changedSaree.length || back.gone) ? back : null);
        }
        linesDate.current = date;
      } catch {
        notifications({ title: t("No connection"), message: t("The sheet needs a connection."), type: "warning" });
      }
    },
    [date, defaultType] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    // The rows typed on the day we're leaving stay in that day's device copy.
    flushDraft();
    touched.current = new Set();
    setElsewhere(otherDrafts(scope, date));
    setData(null);
    setRestoredNote(null);
    load(false);
    const cur = history.location;
    history.replace({ ...cur, search: withParams(cur.search, { date: date === todayIso() ? null : date }) });
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // The parts arrived after the day: rebuild the rows with the default part, keeping anything typed.
  useEffect(() => {
    if (defaultType) load(true);
  }, [defaultType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every change to the typed rows goes to this phone (debounced), so a reload never loses them.
  useEffect(() => {
    const timer = window.setTimeout(flushDraft, 300);
    return () => window.clearTimeout(timer);
  }, [lines, confirmed, flushDraft]);

  useEffect(() => {
    cleanOldDrafts();
    // Closing the tab or reloading with unsaved rows: the browser asks first (the copy is written anyway).
    const onUnload = (e: BeforeUnloadEvent) => {
      flushDraft();
      if (!draftLines(linesRef.current, confirmedRef.current).length) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    // A session that expires clears storage: it writes this screen's rows first.
    const unregister = registerDraftFlush(flushDraft);
    return () => {
      unregister();
      window.removeEventListener("beforeunload", onUnload);
      flushDraft();
    };
  }, [flushDraft]);

  // Discard removes only the rows put back from this phone: another tab's rows and loom-page lines stay.
  const discardRestored = () => {
    if (draftScope() === scope) writeDraft(scope, date, [], [], restoredNote?.restoredIds || []);
    touched.current = new Set();
    linesDate.current = null;
    setRestoredNote(null);
    load(false);
  };

  const lineByLoom = useMemo(() => new Map(lines.map((l) => [l.loomId, l])), [lines]);
  const mine = useMemo(() => roundLoomIds(data), [data]);
  // Rows come from the server with round looms first; My round narrows to them, All shows every loom.
  const scoped = mine && roundScope === "MINE" ? rows.filter((r) => mine.has(r.loomId)) : rows;
  const counts = countRows(scoped);
  const shown = scoped.filter((r) => matchesFilter(r, filter));
  const ready = savable(lines, confirmed);
  const unconfirmed = waitingForPeople(lines, confirmed);
  const partless = waitingForPart(lines);
  const running = scoped.filter((r) => r.kind === "RUN");

  const patch = (loomId: string, p: Partial<SheetLine>) => {
    touched.current.add(loomId);
    if (changesPeople(p)) confirmLoom(loomId);
    setLines((ls) => ls.map((l) => (l.loomId === loomId && l.status !== "SAVED" ? { ...l, ...p, status: "EDIT", message: undefined } : l)));
  };

  const paste = (loomId: string, text: string) => {
    const at = lines.findIndex((l) => l.loomId === loomId);
    const next = at >= 0 ? pasteColumn(lines, at, text) : null;
    if (next) {
      next.forEach((l, i) => l !== lines[i] && touched.current.add(l.loomId));
      setLines(next);
    }
    return !!next;
  };

  const saveAll = useCallback(async () => {
    const todo = savable(linesRef.current, confirmedRef.current);
    // A double tap can't start a second run before the first one re-renders.
    if (!todo.length || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    let ok = 0;
    let failed = 0;
    // The day being saved: the date can still change (Back, the address) while the saves run.
    const day = date;
    // Also updates the ref at once: the reload below must see each line's outcome even before React re-renders.
    // If the screen has moved to another day, the outcome goes to this day's device copy instead, never
    // onto the other day's row for the same loom.
    const setLine = (line: SheetLine, p: Partial<SheetLine>) => {
      if (linesDate.current !== day) {
        if (p.status === "SAVED") patchDraftLine(scope, day, line.loomId, null);
        else if (p.status === "ERROR") patchDraftLine(scope, day, line.loomId, { status: "ERROR", message: p.message });
        return;
      }
      touched.current.add(line.loomId);
      const apply = (ls: SheetLine[]) => ls.map((l) => (l.key === line.key ? { ...l, ...p } : l));
      linesRef.current = apply(linesRef.current);
      setLines(apply);
    };
    try {
      for (const line of todo) {
        setLine(line, { status: "SAVING" });
        try {
          const res: any = await saveLoomEntry(entryBody(line, day));
          if (res?.success) {
            ok++;
            setLine(line, { status: "SAVED" });
          } else {
            failed++;
            setLine(line, { status: "ERROR", message: t(res?.message || "Not saved") });
          }
        } catch {
          failed++;
          setLine(line, { status: "ERROR", message: t("No connection. Try again.") });
        }
      }
    } finally {
      // Whatever happens above (a draft write, a re-render), Save is never left disabled.
      savingRef.current = false;
      setSaving(false);
    }
    await load();
    notifications({
      title: failed ? t("Some looms not saved") : t("Saved"),
      message: failed ? t("{{ok}} saved, {{failed}} need a look (marked in red).", { ok, failed }) : ok === 1 ? t("1 loom saved.") : t("{{ok}} looms saved.", { ok }),
      type: failed ? "warning" : "success",
    });
  }, [date, load, saving]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ctrl+S / ⌘S saves everything typed, like the sheet it replaces.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveAll();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saveAll]);

  // Desktop: the pay for typed inches, worked out by the server as you type.
  const sigs = useRef(new Map<string, string>());
  useEffect(() => {
    if (tier !== "table") return;
    const timer = window.setTimeout(() => {
      lines.forEach((l) => {
        if (l.status === "SAVED" || !(Number(l.inches) > 0) || !l.weaverId) return;
        const sig = [l.inches, l.weaverId, l.helperId, l.workTypeId].join("|");
        if (sigs.current.get(l.loomId) === sig) return;
        sigs.current.set(l.loomId, sig);
        previewLoomEntry(entryBody(l, date))
          .then((res: any) => setPreviews((m) => new Map(m).set(l.loomId, res?.success ? res.data?.calc || {} : {})))
          .catch(() => undefined);
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [lines, tier, date]);

  /** Enter moves to the next loom still to fill; after the last one it saves. */
  const enterNext = (loomId: string) => {
    const order = shown.filter((r) => r.kind === "RUN").map((r) => r.loomId);
    const after = order.slice(order.indexOf(loomId) + 1);
    const next = after.map((id) => inputs.current.get(id)).find((el) => el && !el.disabled);
    if (next) next.focus();
    else if (ready.length) saveAll();
  };

  const registerInput = (loomId: string, el: HTMLInputElement | null) => {
    if (el) inputs.current.set(loomId, el);
    else inputs.current.delete(loomId);
  };

  const openLoom = (loomId: string) => {
    if (tier === "split") setSelectedId(loomId);
    else if (tier === "table") setDrawerId(loomId);
    else history.push(kp.rounds({ date: date !== todayIso() ? date : null, loom: loomId }));
  };

  const run = async (fn: () => Promise<any>, done: string) => {
    setBusy(true);
    try {
      const res: any = await fn();
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return false;
      }
      notifications({ title: t("Saved"), message: done, type: "success" });
      await load();
      return true;
    } catch {
      notifications({ title: t("No connection"), message: t("This needs a connection. Try again in a moment."), type: "warning" });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reopen = (loomId: string) => {
    const r = rows.find((x) => x.loomId === loomId);
    run(() => reopenLoom(loomId, { date }), t("Loom {{n}} reopened", { n: r?.loomCode || "" }));
  };

  const closeWith = async (loomId: string, reason: string) => {
    const r = rows.find((x) => x.loomId === loomId);
    if (await run(() => closeLoom(loomId, { reason, date }), t("Loom {{n}} closed", { n: r?.loomCode || "" }))) setActionsFor(null);
  };

  if (!canUseRounds(role)) return null;

  const loomById = (id: string | null) => (id ? (data?.looms || []).find((l: any) => l.id === id) || null : null);
  const doneLabel =
    running.length || counts.CLOSED
      ? `${t("{{done}} of {{n}} looms done", { done: counts.DONE, n: running.length })}${counts.CLOSED ? ` · ${t("{{n}} closed", { n: counts.CLOSED })}` : ""}`
      : "";
  const isToday = date === todayIso();

  const filters = (
    <div className="kd-seg" role="group" aria-label={t("Show")}>
      {(
        [
          ["ALL", t("All")],
          ["LEFT", t("Left")],
          ["DONE", t("Done")],
          ["CLOSED", t("Closed")],
        ] as [DailyFilter, string][]
      ).map(([k, label]) => (
        <button key={k} type="button" className={filter === k ? "is-on" : ""} aria-pressed={filter === k} onClick={() => setFilter(k)}>
          {label} {data ? counts[k] : "…"}
        </button>
      ))}
    </div>
  );

  // Looms outside the round still waiting for today's work: said quietly, one tap to see them.
  const outsidePending = mine && roundScope === "MINE" ? rows.filter((r) => !mine.has(r.loomId) && matchesFilter(r, "LEFT")).length : 0;
  const roundSwitch = mine ? (
    <>
      <RoundScopeSwitch scope={roundScope} onChange={setRoundScope} count={mine.size} />
      {tier === "table" ? null : <OutsideRoundNote pending={outsidePending} onShowAll={() => setRoundScope("ALL")} />}
    </>
  ) : null;

  const dayNav = (
    <div className="kd-day">
      <button type="button" className="kd-iconbtn" aria-label={t("Previous day")} disabled={saving} onClick={() => setDate(shiftDay(date, -1))}>
        ‹
      </button>
      <label className="kd-day__pick">
        <span>{isToday ? `${t("Today")} · ${dayLabel(date, lang)}` : dayLabel(date, lang)}</span>
        <input
          type="date"
          value={date}
          max={todayIso()}
          disabled={saving}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          // Desktop browsers only open the calendar from its icon; open it from anywhere on the box.
          onClick={(e) => (e.currentTarget as any).showPicker?.()}
          aria-label={t("Date")}
        />
      </label>
      <button type="button" className="kd-iconbtn" aria-label={t("Next day")} disabled={isToday || saving} onClick={() => setDate(shiftDay(date, 1))}>
        ›
      </button>
    </div>
  );

  const saveLabel = saving ? t("Saving…") : ready.length === 1 ? t("Save 1 loom") : ready.length ? t("Save {{n}} looms", { n: ready.length }) : t("Save");

  const listProps = {
    rows: shown,
    lines: lineByLoom,
    date,
    partName: (id?: string) => {
      return partName(types.find((x) => x.id === id), lang, t);
    },
    onType: (loomId: string, inches: string) => patch(loomId, { inches }),
    onPaste: paste,
    onEnter: enterNext,
    registerInput,
    onReopen: reopen,
    onStart: openLoom,
    onLongPress: (id: string) => setActionsFor(id),
    confirmed,
    onConfirm: confirmLoom,
    types,
    onPart: (loomId: string, workTypeId: string) => patch(loomId, { workTypeId }),
    personOf: (id?: string) => (id ? picked.get(id) || roster.find((x) => x.id === id) || null : null),
  };

  const empty =
    data && !rows.length ? (
      <div className="kw-empty">
        {t("No looms yet.")}{" "}
        <button type="button" className="btn btn-link p-0" onClick={() => history.push(kp.home())}>
          {t("Add your looms")}
        </button>
      </div>
    ) : data && !shown.length ? (
      <div className="kw-empty">{t("Nothing here for this filter.")}</div>
    ) : null;

  const actionRow = rows.find((r) => r.loomId === actionsFor) || null;
  const selected = loomById(selectedId) || (tier === "split" ? loomById(shown.find((r) => r.kind !== "CLOSED")?.loomId || shown[0]?.loomId || null) : null);
  const looms: any[] = data?.looms || [];
  const neighbour = (id: string, step: number) => {
    const order = shown.map((r) => r.loomId);
    const at = order.indexOf(id);
    const other = at >= 0 ? looms.find((l) => l.id === order[at + step]) : null;
    return other ? { id: other.id, code: other.code } : null;
  };
  const afterSave = async ({ advance }: { advance: boolean }, current: string) => {
    await load();
    if (!advance) return;
    const order = shown.map((r) => r.loomId);
    const at = order.indexOf(current);
    const next = [...shown.slice(at + 1), ...shown.slice(0, Math.max(0, at))].find((r) => r.kind === "RUN" && r.savedEntries === 0 && r.loomId !== current);
    if (tier === "split") setSelectedId(next ? next.loomId : current);
    else setDrawerId(next ? next.loomId : null);
  };

  return (
    <div className={`gi-registry-page kw-page kd-page is-${tier}${tier === "list" && ready.length ? " has-foot" : ""}`}>
      {tier === "table" ? (
        <div className="kd-top">
          <div className="kd-top__title">
            <h2>{t("Daily work")}</h2>
            {doneLabel ? <span>{doneLabel}</span> : null}
          </div>
          {dayNav}
          {roundSwitch}
          {filters}
          <button type="button" className="kd-btn kd-btn--primary" disabled={saving || !ready.length} onClick={saveAll} title="Ctrl+S">
            {saving ? t("Saving…") : t("Save all · Ctrl+S")}
            {ready.length ? <span className="kd-badge">{ready.length}</span> : null}
          </button>
        </div>
      ) : (
        <>
          <PageHeader
            title={
              <>
                {t("Daily work")}
                {doneLabel ? <small>{doneLabel}</small> : null}
              </>
            }
          >
            <LangToggle />
          </PageHeader>
          {dayNav}
          {roundSwitch}
          {filters}
        </>
      )}

      {tier === "table" ? <OutsideRoundNote pending={outsidePending} onShowAll={() => setRoundScope("ALL")} /> : null}
      {data && unconfirmed.length ? (
        <div className="kr-banner is-info">{t("{{n}} looms wait for their weaver and helper to be confirmed.", { n: unconfirmed.length })}</div>
      ) : null}
      {data && partless.length ? (
        <div className="kr-banner is-info">
          {partless.length === 1 ? t("1 loom needs its part picked before saving.") : t("{{n}} looms need their part picked before saving.", { n: partless.length })}
        </div>
      ) : null}
      {data?.late ? <div className="kr-banner is-warn">{t("This day is 3 or more days back. Entries wait for the admin's approval.")}</div> : null}
      {data && restoredNote ? (
        <div className="kd-draft" role="status">
          {restoredNote.restored ? (
            <span>
              {restoredNote.restored === 1 ? t("1 loom restored from this phone") : t("{{n}} looms restored from this phone", { n: restoredNote.restored })}
              {" · "}
              <button type="button" className="btn btn-link p-0" onClick={discardRestored}>
                {t("Discard")}
              </button>
            </span>
          ) : null}
          {restoredNote.alreadySaved.length ? (
            <span>{t("Loom {{codes}} was already saved for this day; the saved entry is kept and this phone's copy dropped.", { codes: restoredNote.alreadySaved.join(", ") })}</span>
          ) : null}
          {restoredNote.changedSaree.length ? (
            <span>{t("Loom {{codes}} now has a different saree; this phone's copy dropped.", { codes: restoredNote.changedSaree.join(", ") })}</span>
          ) : null}
          {restoredNote.gone ? <span>{t("{{n}} rows from this phone have no saree on this day any more and were dropped.", { n: restoredNote.gone })}</span> : null}
        </div>
      ) : null}
      {elsewhere.length ? (
        <div className="kd-draft">
          {elsewhere.map((d) => (
            <button key={d.date} type="button" className="btn btn-link p-0" disabled={saving} onClick={() => setDate(d.date)}>
              {t("{{n}} unsaved on {{day}}", { n: d.n, day: dayLabel(d.date, lang) })} ›
            </button>
          ))}
        </div>
      ) : null}
      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}

      {data && tier === "table" ? (
        <>
          {empty || (
            <DailyTable
              rows={shown}
              lines={lineByLoom}
              roster={roster}
              types={types}
              previews={previews}
              date={date}
              onPatch={patch}
              confirmed={confirmed}
              onConfirm={confirmLoom}
              onType={listProps.onType}
              onPaste={paste}
              onEnter={enterNext}
              registerInput={registerInput}
              onOpen={openLoom}
              onReopen={reopen}
              onStart={openLoom}
            />
          )}
          <p className="kd-hint">{t("Enter moves down, Tab moves across. Paste a column copied from Excel or Google Sheets. Click a loom number for its page.")}</p>
        </>
      ) : null}

      {data && tier === "split" ? (
        <div className="kd-split">
          <div className="kd-split__list">
            {empty || <DailyList {...listProps} selectedId={selected?.id || null} onOpen={setSelectedId} onName={setSelectedId} />}
            {ready.length ? (
              <button type="button" className="kd-btn kd-btn--primary kd-split__save" disabled={saving} onClick={saveAll}>
                {saveLabel}
              </button>
            ) : null}
          </div>
          <div className="kd-split__pane">
            {selected ? (
              <LoomSheet
                key={selected.id + date}
                asPage
                loom={selected}
                date={date}
                late={!!data?.late}
                workTypes={types}
                orgId={orgId}
                prevLoom={neighbour(selected.id, -1)}
                nextLoom={neighbour(selected.id, 1)}
                onGoTo={setSelectedId}
                onClose={() => setSelectedId(null)}
                onSaved={(o) => afterSave(o, selected.id)}
              />
            ) : (
              <div className="kw-empty">{t("Pick a loom on the left.")}</div>
            )}
          </div>
        </div>
      ) : null}

      {data && tier === "list" ? (
        <>
          {empty || <DailyList {...listProps} onOpen={openLoom} onName={(id) => setActionsFor(id)} />}
          <p className="kd-hint">{t("Tap a name to change who is on the loom today, or to mark it closed. Tap the loom number for its page.")}</p>
          {ready.length ? (
            <div className="kf-foot">
              <button type="button" className="kd-btn kd-btn--primary" disabled={saving} onClick={saveAll}>
                {saveLabel}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {actionRow ? (
        <RowActions
          row={actionRow}
          line={lineByLoom.get(actionRow.loomId)}
          // People added on this screen aren't in the roster yet; without them the picker would fall back to the saree's weaver.
          roster={[...roster, ...Array.from(picked.values()).filter((p) => !roster.some((r) => r.id === p.id))]}
          busy={busy}
          onOpen={() => {
            setActionsFor(null);
            openLoom(actionRow.loomId);
          }}
          onPeople={({ weaverId, helperId, helperMode, people }) => {
            setPicked((m) => {
              const next = new Map(m);
              people.forEach((x) => next.set(x.id, x));
              return next;
            });
            patch(actionRow.loomId, { weaverId, helperId, helperMode });
            setActionsFor(null);
          }}
          onCloseLoom={(reason) => closeWith(actionRow.loomId, reason)}
          onDismiss={() => setActionsFor(null)}
        />
      ) : null}

      {tier === "table" && drawerId && loomById(drawerId) ? (
        <LoomSheet
          key={drawerId + date}
          loom={loomById(drawerId)}
          date={date}
          late={!!data?.late}
          workTypes={types}
          orgId={orgId}
          prevLoom={neighbour(drawerId, -1)}
          nextLoom={neighbour(drawerId, 1)}
          onGoTo={setDrawerId}
          onClose={() => setDrawerId(null)}
          onSaved={(o) => afterSave(o, drawerId)}
        />
      ) : null}
    </div>
  );
};

export default KarkhanaDailyWorkPage;
